import { atomWithStorage } from "jotai/utils";

/**
 * An in-flight "move to withdrawal wallet" transfer (Step 1 of the withdraw
 * dialog). `withdraw3` resolves on request *acceptance*, but the L1 → Arbitrum
 * settlement takes ~5 min, so the dialog persists the transfer across dialog
 * close / page refresh to keep showing the "funds in transit" state and to
 * resume at Step 2.
 *
 * There is no Hyperliquid API for bridge-settlement status, so arrival is
 * detected purely on-chain: the wallet USDC balance rising above
 * `balanceBefore` (the balance captured at submit time). Watching for *any*
 * increase — rather than an absolute `>= amount` target — keeps detection
 * robust to the $1 withdraw fee (received ≈ amount − 1) and to any
 * pre-existing wallet balance.
 *
 * Lifecycle: in-flight ("funds in transit") → on arrival the record is stamped
 * with `completedAt` and kept for a short "completed" check window
 * (`PENDING_WITHDRAWAL_COMPLETED_VISIBLE_MS`) so indicators can flash a done
 * state — then deleted. The sole writer is withdraw-dialog's lifecycle effect.
 */
export type PendingWithdrawal = {
	/** USDC amount moved in Step 1 — recorded for reference. The Step 2 input is
	 *  NOT prefilled from this; it auto-fills with the actual arrived balance. */
	amount: string;
	/** On-chain wallet USDC at submit time — the arrival-detection baseline. */
	balanceBefore: number;
	/** Epoch ms when `withdraw3` was submitted (for TTL expiry). */
	ts: number;
	/** Epoch ms when arrival was first observed — starts the "completed" check window. */
	completedAt?: number;
};

/** Per-address record of an in-flight transfer, keyed by lowercased address. */
export const pendingWithdrawalsAtom = atomWithStorage<
	Record<string, PendingWithdrawal>
>("trade_pending_withdrawals_v1", {});

/** Auto-expire a stuck record (a withdrawal that never visibly arrived). */
export const PENDING_WITHDRAWAL_TTL_MS = 30 * 60_000;

/** Float guard for the "balance rose above baseline" arrival check. */
export const PENDING_WITHDRAWAL_EPSILON = 0.000001;

/** How long the post-arrival "completed" check stays visible before the record is dropped. */
export const PENDING_WITHDRAWAL_COMPLETED_VISIBLE_MS = 10_000;

/** True once the wallet balance has risen above the submit-time baseline. */
export function pendingWithdrawalArrived(
	record: PendingWithdrawal,
	walletBalance: number,
): boolean {
	return walletBalance > record.balanceBefore + PENDING_WITHDRAWAL_EPSILON;
}

/** True once the record is older than the TTL (failsafe cleanup). */
export function pendingWithdrawalExpired(
	record: PendingWithdrawal,
	now: number,
): boolean {
	return now - record.ts >= PENDING_WITHDRAWAL_TTL_MS;
}

/**
 * A transfer is "in transit" while a record exists, has not expired, and the
 * wallet balance has not yet risen above the submit-time baseline.
 */
export function pendingWithdrawalInTransit(
	record: PendingWithdrawal | undefined,
	walletBalance: number,
	now: number,
): boolean {
	if (!record) return false;
	// Once completed, never "in transit" again — even if the balance dips back
	// below the baseline during the check window (e.g. an immediate deposit).
	if (record.completedAt !== undefined) return false;
	return (
		!pendingWithdrawalExpired(record, now) &&
		!pendingWithdrawalArrived(record, walletBalance)
	);
}

/** True while the post-arrival "completed" check should be visible. */
export function pendingWithdrawalJustCompleted(
	record: PendingWithdrawal | undefined,
	now: number,
): boolean {
	if (record?.completedAt === undefined) return false;
	return now - record.completedAt < PENDING_WITHDRAWAL_COMPLETED_VISIBLE_MS;
}
